import { get } from 'svelte/store';
import { designElementSummary } from '../routes/design/[productSlug]/_design-functions.js';
import { calculateTotalPrice } from '../utils/pricing.js';

/**
 * Builds the full itemSummary object for all selected products,
 * including design previews (async) and size summaries.
 *
 * @param {Object} options
 * @param {string} options.designId                   - The Konva stage ID (i.e., your designId)
 * @param {Object} options.designState               - The exported designState object (with methods)
 * @param {Object} options.productDataStore          - The raw store value of productDataStore
 * @param {Object} options.teamDetails               - The raw store value of teamDetails
 * @param {boolean} options.numberInputRequired      - Whether a number input is required
 * @param {boolean} options.nameInputRequired        - Whether a name input is required
 * @param {Array} options.defaultBulkDiscounts       - The default bulk discounts for the product
 * @returns {Promise<{ itemSummary: Object, noSizesSelected: boolean }>}
 */
export async function generateItemSummaries({
  designId,
  designState,
  productDataStore,
  teamDetails,
  numberInputRequired,
  nameInputRequired,
  defaultBulkDiscounts,
}) {
  // Identify which items (top/bottom/hat/etc.) are selected
  const arrayOfSelectedProducts = designState.arrayOfSelectedProducts(designId);
  if (!arrayOfSelectedProducts || arrayOfSelectedProducts.length === 0) {
    return {
      itemSummary: null,
      noSizesSelected: true,
    };
  }

  // Prepare a base itemSummary structure
  let itemSummary = {
    top: createEmptyItemObject(),
    bottom: createEmptyItemObject(),
    hat: createEmptyItemObject(),
    socks: createEmptyItemObject(),
    accessory: createEmptyItemObject(),
    totalPrice: 0,
    category: productDataStore.category,
    collection: productDataStore.collection,
    customised: false,
    includesEmbroidery: false,
  };

  // We'll gather image Promises so we can await them all at once
  let imagePromises = [];
  let anyItemCustomised = false;

  // Loop through each selected product (e.g., "top", "bottom", etc.)
  for (const item of arrayOfSelectedProducts) {
    if (!productDataStore[item]) continue;

    const productInfo = productDataStore[item];
    const $designState = get(designState);
    const designObj = $designState[0]?.[item]; // only one design for now

    // Build size summary
    let totalCount = 0;
    const allSizes = productInfo.sizes || [];

    for (const sizeObj of allSizes) {
      const filteredTeamDetails = teamDetails.filter((entry) => entry[item + 'SizeId'] === sizeObj.ProductSize.id);
      let count = filteredTeamDetails.length;

      // If you're not in "team grid mode," read a single .qty
      // from filteredTeamDetails[0] instead:
      if (!numberInputRequired && !nameInputRequired) {
        if (filteredTeamDetails[0] && filteredTeamDetails[0][item + 'Qty']) {
          count = filteredTeamDetails[0][item + 'Qty'];
        }
      }

      if (count > 0) {
        itemSummary[item].sizeSummary.push({
          size: sizeObj.ProductSize,
          count,
        });
        totalCount += count;
      }
    }

    // Update item count
    itemSummary[item].count = totalCount;

    const bulkDiscounts = productInfo.bulkDiscounts?.length || defaultBulkDiscounts;
    const { total, unitPrice, applicableDiscount } = calculateTotalPrice({
      basePrice: productInfo.price,
      designElements: designObj.designElements,
      qty: itemSummary[item].count,
      sponsorship: productInfo.sponsorship,
      discountTiers: bulkDiscounts,
    });
    const selectedColour = productInfo.selectedColour;
    const deliveryTimes = productInfo.deliveryTimes;

    // Populate base fields
    itemSummary[item].name = productInfo.name;
    itemSummary[item].price = total;
    itemSummary[item].fullPrice = unitPrice;
    itemSummary[item].discountPercent = applicableDiscount?.discountPercent || 0;
    itemSummary[item].discountRange = applicableDiscount?.range;
    itemSummary[item].colour = selectedColour.displayName;
    itemSummary[item].deliveryTimes = deliveryTimes;

    // Summarize design elements
    if (!designObj) continue;
    itemSummary[item].designSummary.front = designElementSummary(designObj.designElements.front);
    itemSummary[item].designSummary.back = designElementSummary(designObj.designElements.back);
    itemSummary[item].designSummary.leftSide = designElementSummary(designObj.designElements.leftSide);
    itemSummary[item].designSummary.rightSide = designElementSummary(designObj.designElements.rightSide);

    if (
      itemSummary[item].designSummary.front?.summary.includes('embroidery') ||
      itemSummary[item].designSummary.back?.summary.includes('embroidery') ||
      itemSummary[item].designSummary.leftSide?.summary.includes('embroidery') ||
      itemSummary[item].designSummary.rightSide?.summary.includes('embroidery')
    ) {
      itemSummary.includesEmbroidery = true;
    }

    // Enqueue async image generation for front/back previews
    // (assuming designState.generateDesignPreview returns a Promise that resolves to a dataURL)
    imagePromises.push(
      Promise.all([
        designState.generateDesignPreview({
          designId,
          item,
          side: 'front',
          gender: productInfo.gender,
          isModelBasedImages: productInfo.isModelBasedImages,
        }),
        designState.generateDesignPreview({
          designId,
          item,
          side: 'back',
          gender: productInfo.gender,
          isModelBasedImages: productInfo.isModelBasedImages,
        }),
      ]).then(([frontImg, backImg]) => {
        itemSummary[item].frontPreview = frontImg;
        itemSummary[item].backPreview = backImg;
      }),
    );

    // Update counts & total pricing
    itemSummary[item].total = totalCount * itemSummary[item].price;
    itemSummary[item].fullTotal = totalCount * itemSummary[item].fullPrice;
    itemSummary.totalPrice += itemSummary[item].total;

    // Determine if item is “customised”
    const basePrice = designObj.product.price; // the original product price
    if (itemSummary[item].price !== basePrice) {
      itemSummary[item].isCustomised = true;
      anyItemCustomised = true;
    }
  }

  // Mark if any item is customised
  itemSummary.customised = anyItemCustomised;

  // Wait for all preview images to finish generating
  await Promise.all(imagePromises);

  // Check if *any* sizes were selected
  let totalCounts =
    itemSummary.top.count +
    itemSummary.bottom.count +
    itemSummary.hat.count +
    itemSummary.socks.count +
    itemSummary.accessory.count;
  const noSizesSelected = totalCounts === 0;

  // Return final results
  return {
    itemSummary,
    noSizesSelected,
  };
}

/**
 * Helper that returns the initial shape of a single item’s summary.
 */
function createEmptyItemObject() {
  return {
    name: null,
    count: 0,
    price: 0,
    fullPrice: 0,
    discountPercent: 0,
    discountRange: null,
    total: 0,
    fullTotal: 0,
    colour: null,
    isCustomised: false,
    frontPreview: null,
    backPreview: null,
    sizeSummary: [],
    deliveryTimes: [],
    designSummary: {
      front: null,
      back: null,
      leftSide: null,
      rightSide: null,
    },
  };
}
